import React from "react";
import { Link } from "react-router-dom";
import Footer from "../../components/Footer/Footer";
import Navbar from "../../components/navbar/index.jsx";
import WaFloat from "../../components/WaFloat/WaFloat.jsx";
import "./MainBody.css";
import aqtVideo from "../../assets/videos/aqt.mp4";
import heroImg from "../../assets/images/gallery/16.png";
import { useReveal } from "../../hooks/useReveal.js";

const stats = [
  { num: "Startup", lbl: "to established corporate" },
  { num: "6", lbl: "High-growth sectors" },
  { num: "3", lbl: "Advisory service lines" },
  { num: "Pan-India", lbl: "Investor network" },
];

const whoList = [
  { n: "01", h: "Promoters & Founders", p: "Growth-stage businesses seeking capital or strategic direction." },
  { n: "02", h: "Family Offices", p: "Seeking curated private investment opportunities in Indian mid-market." },
  { n: "03", h: "Growth Companies", p: "Established businesses planning large-scale expansion or capital events." },
  { n: "04", h: "Private Investors", p: "HNIs seeking structured access to pre-market opportunities." },
  { n: "05", h: "Cross-border Opportunities", p: "International businesses or investors seeking India entry advisory." },
];

const services = [
  { n: "I",   h: "Strategic Advisory",  p: "Business strategy, capital readiness, and growth planning. We structure the opportunity before we introduce the capital.", to: "/businesses" },
  { n: "II",  h: "Capital Introductions", p: "Curated introductions to HNIs, family offices, and institutional partners. Confidential. Structured. Purposeful.", to: "/businesses" },
  { n: "III", h: "AFO",  p: "The AQT Financial Officer model — an embedded senior advisor who makes your business investor-ready from within.", to: "/afo" },
  { n: "IV",  h: "Expansion Strategy",  p: "Market entry, JV structuring, and cross-border advisory for businesses scaling into new geographies or segments.", to: "/businesses" },
];

const whyPoints = [
  { h: "Selective Mandates",     p: "We work with few clients at a time. Your engagement receives senior attention, not junior delegation." },
  { h: "Senior-Led Execution",   p: "Every engagement is led by principals with 20+ years of institutional experience. No hand-offs." },
  { h: "Confidential Process",   p: "We do not publicise our clients, mandates, or transactions. Discretion is a foundational value, not a feature." },
  { h: "Strong Networks",        p: "Built from decades at JP Morgan, Morgan Stanley, and State Bank of India. Relationships, not databases." },
  { h: "India + Global Reach",   p: "Domestic depth with international perspective. Cross-border capital flows and market entry advisory." },
];

const insightCards = [
  { tag: "Mid-Market",     h: "India's Mid-Market Capital Opportunity",       p: "The ₹10–500 Cr capital band remains structurally underserved. Why the mid-market is the most compelling advisory opportunity in Indian private finance today." },
  { tag: "Family Offices", h: "Family Office Trends in Indian Private Capital", p: "How India's family offices are evolving from passive wealth preservation to active deal participation — and what it means for advisory platforms." },
  { tag: "Cross-Border",   h: "Cross-Border Capital Flows into India",         p: "NRI family offices, international strategic investors, and the structural tailwinds driving capital into Indian mid-market businesses." },
];

export default function MainBody({ headings, idpoly, images }) {
  useReveal();

  return (
    <>
      <Navbar />

      {/* ── AQTLanding Hero ── */}
      <div className="main-banner">
        <video autoPlay loop muted playsInline className="banner-video">
          <source src={aqtVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="banner-content">
          <h1>SCALE YOUR BUSINESS</h1>
          <p>
            Achieve exponential growth with AQT's tailored{" "}
            <span className="break-after">financial</span> solutions designed to
            meet your business needs.
          </p>
        </div>
      </div>

      {/* ── aqt-v2 Hero ── */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-pill reveal">
            <div className="pulse" />
            <span>Where Business Leaders and Investors Build Together</span>
          </div>
          <h1 className="hero-h reveal reveal-delay-1">Build Growth.<br /><em>Unlock Capital.</em><br />Scale Faster.</h1>
          <p className="hero-sub reveal reveal-delay-2">
            We help businesses become investor-ready, structure growth plans, and connect with aligned capital partners — from startups raising their first round to established corporates planning large-scale expansion.
          </p>
          <div className="hero-btns reveal reveal-delay-3">
            <Link className="btn-primary" to="/contactus">Start the Conversation</Link>
            <Link className="btn-outline" to="/investors">Join Investor Network</Link>
          </div>
          <div className="hero-stats reveal reveal-delay-4">
            {stats.map(({ num, lbl }) => (
              <div key={lbl}>
                <div className="stat-num"><span>{num}</span></div>
                <div className="stat-lbl">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-img-panel">
          <img
            src={heroImg}
            alt="AQT Direct advisory office"
          />
        </div>
      </section>


      {/* ── aqt-v2 Who We Work With ── */}
      <section className="section">
        <span className="sec-tag reveal">Who we work with</span>
        <div className="grid-2 grid-2-center">
          <div>
            <h2 className="sec-h reveal">Selective engagements.<br /><em>Sustained outcomes.</em></h2>
            <p className="sec-sub reveal reveal-delay-1">AQT Direct advises a select group of promoters, businesses and investors. We do not work with everyone. We choose engagements where we can add genuine, measurable value.</p>
            <p className="sec-sub reveal reveal-delay-1" style={{ marginTop: 8 }}>Our approach is discreet, senior-led, and outcome-focused. Every engagement receives direct attention from our principals.</p>
            <div className="reveal reveal-delay-2" style={{ marginTop: 28 }}>
              <Link className="btn-ghost" to="/about">About AQT Direct</Link>
            </div>
          </div>
          <div className="who-list reveal reveal-delay-1">
            {whoList.map(({ n, h, p }) => (
              <div className="who-item" key={n}>
                <span className="who-num">{n}</span>
                <div className="who-content"><h4>{h}</h4><p>{p}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <hr className="rule" />

      {/* ── aqt-v2 Advisory Services ── */}
      <section className="section section-off">
        <span className="sec-tag reveal">Advisory services</span>
        <h2 className="sec-h reveal">Four disciplines.<br /><em>One integrated practice.</em></h2>
        <div className="svc-grid">
          {services.map(({ n, h, p, to }, i) => (
            <div className={`svc reveal${i > 0 ? ` reveal-delay-${Math.min(i, 4)}` : ""}`} key={n}>
              <span className="svc-n">{n}</span>
              <h3>{h}</h3>
              <p>{p}</p>
              <Link className="svc-link" to={to}>Explore <span>→</span></Link>
            </div>
          ))}
        </div>
      </section>

      <hr className="rule" />

      {/* ── aqt-v2 Why AQT Direct ── */}
      <section className="section">
        <span className="sec-tag reveal">Why AQT Direct</span>
        <div className="grid-2 grid-2-center">
          <div>
            <h2 className="sec-h reveal">We advise select businesses on growth, capital, <em>and strategic expansion.</em></h2>
            <p className="sec-sub reveal reveal-delay-1" style={{ marginTop: 20 }}>Our mandate list is deliberately short. That is not a constraint — it is a commitment to quality of engagement, depth of attention, and confidentiality of process.</p>
          </div>
          <div className="why-points reveal reveal-delay-1">
            {whyPoints.map(({ h, p }) => (
              <div className="why-pt" key={h}>
                <div className="why-pt-mark" />
                <div className="why-pt-body"><h4>{h}</h4><p>{p}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── aqt-v2 Insights ── */}
      <section className="section section-off">
        <span className="sec-tag reveal">Insights</span>
        <h2 className="sec-h reveal">Perspectives on capital,<br />growth <em>&amp; strategy.</em></h2>
        <div className="insights-grid">
          {insightCards.map(({ tag, h, p }, i) => (
            <Link className={`insight-card reveal${i > 0 ? ` reveal-delay-${i}` : ""}`} to="/insights" key={h} style={{ textDecoration: "none" }}>
              <span className="insight-tag">{tag}</span>
              <h3>{h}</h3>
              <p>{p}</p>
              <span className="insight-read">Read Perspective <span>→</span></span>
            </Link>
          ))}
        </div>
      </section>

      {/* ── aqt-v2 CTA Strip ── */}
      <div className="cta-strip reveal">
        <h2>A conversation costs nothing.<br /><em>The right introduction changes everything.</em></h2>
        <p>We engage with a limited number of new relationships each quarter. If you believe there is a basis for discussion, we invite you to reach out.</p>
        <div className="cta-btns">
          <Link className="btn-white" to="/contactus">Register Interest</Link>
          <Link className="btn-white-outline" to="/investors">Join Investor Network</Link>
        </div>
      </div>

      <Footer />
      <WaFloat />
    </>
  );
}
